import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import Spacing from '../../Spacing';
import SectionHeadingStyle3 from '../../SectionHeading/SectionHeadingStyle3';
import { pageTitle } from '../../../helpers/PageTitle';
import LoadingSpinner from '../../common/LoadingSpinner';

export default function Success() {
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trackingStatus, setTrackingStatus] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const invoiceRef = useRef();
  
  pageTitle('Order Success');

  useEffect(() => {
    const order = location.state?.orderDetails;
    if (!order) {
      navigate('/shop');
      return;
    }

    setOrderDetails(order);
    fetchOrderStatus(order.orderNumber);
    sendConfirmationEmail(order);
    setLoading(false);
  }, [location.state, navigate]);

  const fetchOrderStatus = async (orderNumber) => {
    try {
      const response = await fetch(`/api/orders/${orderNumber}/status`);
      const data = await response.json();
      setTrackingStatus(data.status);
    } catch (error) {
      console.error('Error fetching order status:', error);
    }
  };

  const sendConfirmationEmail = async (order) => {
    try {
      const response = await fetch('/api/orders/send-confirmation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(order)
      });
      if (response.ok) {
        setEmailSent(true);
      }
    } catch (error) {
      console.error('Error sending confirmation email:', error);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => invoiceRef.current,
    documentTitle: `Invoice-${orderDetails?.orderNumber}`,
  });

  if (loading) {
    return <LoadingSpinner text="Loading order details..." />;
  }

  return (
    <>
      <Spacing lg="70" md="70" />
      <Spacing lg="140" md="80" />
      <SectionHeadingStyle3
        title="Order Success"
        subTitle="Continue shopping"
        variant="text-center"
        href="/shop"
      />
      <Spacing lg="125" md="60" />
      <div className="container">
        {/* Order Status Tracker */}
        <div className="cs_order_status">
          <h3>Order Status</h3>
          <div className={`cs_status_badge ${trackingStatus}`}>
            {trackingStatus || 'Processing'}
          </div>
          <div className="cs_status_timeline">
            <div className={`cs_status_step ${trackingStatus === 'confirmed' ? 'active' : ''}`}>
              Confirmed
            </div>
            <div className={`cs_status_step ${trackingStatus === 'processing' ? 'active' : ''}`}>
              Processing
            </div>
            <div className={`cs_status_step ${trackingStatus === 'shipped' ? 'active' : ''}`}>
              Shipped
            </div>
            <div className={`cs_status_step ${trackingStatus === 'delivered' ? 'active' : ''}`}>
              Delivered
            </div>
          </div>
        </div>

        {/* Email Confirmation Status */}
        {emailSent && (
          <div className="cs_email_confirmation">
            <p>✓ Order confirmation has been sent to your email</p>
          </div>
        )}

        {/* Print Invoice Button */}
        <div className="cs_print_section">
          <button onClick={handlePrint} className="cs_btn cs_style_1">
            <span>Print Invoice</span>
          </button>
        </div>

        <div ref={invoiceRef}>
          <p className="m-0 text-center">
            Thank you! Your order has been received.
          </p>
          <div className="cs_height_95 cs_height_lg_50" />
          <ul className="cs_order-summery">
            <li>
              <p>Order Number:</p>
              <h3>{orderDetails.orderNumber}</h3>
            </li>
            <li>
              <p>Date:</p>
              <h3>{new Date(orderDetails.date).toLocaleDateString()}</h3>
            </li>
            <li>
              <p>Total:</p>
              <h3>${orderDetails.total.toFixed(2)}</h3>
            </li>
            <li>
              <p>Payment method:</p>
              <h3>{orderDetails.paymentMethod}</h3>
            </li>
          </ul>
          <div className="cs_height_50 cs_height_lg_30" />
          <div className="cs_shop-card">
            <h2>Order details</h2>
            <table className="border-bottom-0">
              <tbody>
                <tr className="cs_semi_bold">
                  <td>Products</td>
                  <td className="text-end">Amount</td>
                </tr>
                {orderDetails.items.map((item, index) => (
                  <tr key={index}>
                    <td>{item.name} x {item.quantity}</td>
                    <td className="text-end">${(item.price * item.quantity).toFixed(2)}</td>
                  </tr>
                ))}
                <tr>
                  <td className="cs_semi_bold">Subtotal</td>
                  <td className="text-end">${orderDetails.total.toFixed(2)}</td>
                </tr>
                {orderDetails.discount > 0 && (
                  <tr>
                    <td className="cs_semi_bold">Discount</td>
                    <td className="text-end">-${orderDetails.discount.toFixed(2)}</td>
                  </tr>
                )}
                <tr>
                  <td className="cs_semi_bold">Payment method</td>
                  <td className="text-end">{orderDetails.paymentMethod}</td>
                </tr>
                <tr className="cs_semi_bold">
                  <td className="pb-0">Total</td>
                  <td className="text-end pb-0">${orderDetails.total.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Spacing lg="150" md="80" />
    </>
  );
}
